/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { EntityVersion, ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  none,
  RequestMethod,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { ENV_BASE_URL } from '@gh/config';
import { Observable } from 'rxjs';

import {
  LeadDetailsInitDto,
  LeadDetailsSaveDto,
  LeadDetailsSaveResponseDto,
  LeadListFilter,
  LeadListItemDto,
  SalesManagementReportFilterDto,
} from './lead.dto';
import {
  LeadDetailsInitMapper,
  LeadDetailsSaveMapper,
  LeadDetailsSaveResponseMapper,
  LeadListFilterMapper,
  LeadListItemMapper,
} from './lead.mapper';

@Injectable()
export class LeadRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  getInit(): Observable<LeadDetailsInitDto> {
    return this.httpGet(`${this.crm}/leads/init`, LeadDetailsInitMapper);
  }

  get(id: ID): Observable<LeadDetailsInitDto> {
    return this.httpGet(`${this.crm}/leads/${id}`, LeadDetailsInitMapper);
  }

  create(save: LeadDetailsSaveDto): Observable<LeadDetailsSaveResponseDto> {
    return this.httpPost(
      `${this.bp}/leads`,
      save,
      LeadDetailsSaveMapper,
      LeadDetailsSaveResponseMapper);
  }

  update(save: LeadDetailsSaveDto): Observable<LeadDetailsSaveResponseDto> {
    return this.httpPut(
      `${this.bp}/leads/${save.contactId}`,
      save,
      LeadDetailsSaveMapper,
      LeadDetailsSaveResponseMapper);
  }

  reassign(leadId: ID, version: EntityVersion, salesPersonId: ID): Observable<LeadDetailsInitDto> {
    return this.httpPut(
      `${this.bp}/leads/reassign`,
      {
        contactId: leadId,
        version,
        toSalesPersonId: salesPersonId,
      },
      identity(),
      LeadDetailsInitMapper);
  }

  findAllAsDataSource(filter: LeadListFilter): SlicedDataSource<LeadListItemDto> {
    return this.createSlicedDataSource({
      method: RequestMethod.Get,
      url: `${this.crm}/leads`,
      body: filter,
      requestMapper: LeadListFilterMapper,
      responseMapper: arrayOf(LeadListItemMapper),
    });
  }

  initReportFilter(): Observable<SalesManagementReportFilterDto> {
    return this.httpGet(`${this.crm}/sales-management`, identity());
  }

  updateReportFilter(reportFilter: SalesManagementReportFilterDto): Observable<void> {
    return this.httpPut(`${this.crm}/sales-management`, reportFilter, identity(), none());
  }
}
